import React, {useState, useEffect, useCallback, useContext} from 'react';
import {Link} from "react-router";
import {Button, ContextMenu, Icon, Menu, MenuDivider, MenuItem, Popover, Tag, Tooltip} from '@blueprintjs/core';
import styled from 'styled-components';

import {Card} from '/src/components/card'
import {SearchableDataGrid} from '/src/components/tables/data-grid'
import {errorToast, successToast} from "/src/utils/toaster";
import {device} from '/src/services/iot-api'
import {cameraServers, recordings, processingConfig, statElems} from '/src/services/api'

import {ElemsPreview} from '@auravisionlabs/aura-react';

import {NetworkControl} from "./network";
import {ThumbnailsControl} from "./thumbnails";
import {DeviceTopOverlay} from "./device-top";
import {DecommissionDialog} from "./decommission";
import {LiveStreamOverlay} from "./live-stream";
import {DataPageContext} from "../../context";
import {useLayout} from '/src/layouts/devices-sizes';

const ContentWrapper = styled.div`
    margin: 2em;
`;

const TopLayout = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 2em;
    align-items: flex-start;
    justify-content: center;
    margin-bottom: 2em;
`;

const LeftPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1em;

    .row-cam-on {
        background-color: rgba(0, 128, 0, 0.08);
    }

    .row-cam-off {
        background-color: rgba(139, 0, 0, 0.08);
    }

    .row-cam-pending {
        background-color: rgba(200, 170, 0, 0.08);
    }
`;

const StatusRow = styled.div`
    display: flex;
    gap: 1em;
`;

const StatusButton = styled.div`
    flex: 1 1 0;
    max-width: 25em;

    .bp6-intent-danger {
        background-color: #8B0000;
    }
`;

const RightPanel = styled.div`
    flex: 1 1 300px;
    display: flex;
    flex-direction: column;
    gap: 1em;
`;


const _parseRtspIp = (url) => {
    if (!url) return '';
    try {
        const match = url.match(/rtsp:\/\/(?:.*@)?([^:/]+)/);
        return match ? match[1] : '';
    } catch {
        return '';
    }
}

const _buildRecordingColumns = (onStartStream) => [
    {
        key: 'id',
        name: 'Preview',
        width: 200,
        sortable: false,
        filterable: true,
        formatter: ({row}) => {
            const menu = (
                <Menu>
                    <MenuItem
                        icon="eye-open"
                        text="Live stream"
                        onClick={() => onStartStream(row)}
                    />
                    <MenuItem
                        icon="share"
                        text="Open in new tab"
                        href={'/recs/' + row.id}
                        target="_blank"
                    />
                    <MenuDivider />
                    <MenuItem
                        icon="clipboard"
                        text="Copy recording ID"
                        onClick={() => navigator.clipboard.writeText(row.id)}
                    />
                    {row.processingConfig?.provider?.url &&
                        <MenuItem
                            icon="link"
                            text="Copy RTSP URL"
                            onClick={() => navigator.clipboard.writeText(row.processingConfig.provider.url)}
                        />
                    }
                </Menu>
            );
            return (
                <div style={{position: 'relative', width: '100%', height: '100%'}}>
                    <ContextMenu content={menu}>
                        <Link to={'/recs/' + row.id} style={{display: 'block', width: '100%', height: '100%'}}>
                            {row?.thumbnails?.[0]?.url ? (
                                <ElemsPreview recording={row} maxHeight={150} />
                            ) : (
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%'}}>
                                    <Icon icon="media" size={40} color="#8a9ba8" />
                                </div>
                            )}
                        </Link>
                    </ContextMenu>
                    <div style={{position: 'absolute', top: 4, left: 4}}>
                        <Tag
                            intent="success"
                            icon="eye-open"
                            interactive
                            round
                            style={{cursor: 'pointer', minHeight: 18, minWidth: 18, fontSize: 10, padding: '2px 4px'}}
                            onClick={(e) => {
                                e.preventDefault();
                                onStartStream(row);
                            }}
                        />
                    </div>
                    <div style={{position: 'absolute', top: 4, right: 4}}>
                        <Popover content={menu} placement="bottom-end">
                            <Tag
                                intent="primary"
                                icon="more"
                                interactive
                                round
                                style={{cursor: 'pointer', minHeight: 18, minWidth: 18, fontSize: 10, padding: '2px 4px'}}
                                onClick={(e) => e.preventDefault()}
                            />
                        </Popover>
                    </div>
                </div>
            );
        },
    },
    {
        key: 'ip',
        name: 'IP Address',
        width: 150,
        resizable: true,
        sortable: false,
    },
    {
        key: 'status',
        name: 'Status',
        width: 120,
        sortable: false,
        formatter: ({row}) =>
            <Tag
                intent={
                    row.status === 'Online' ? 'success'
                    : row.status === 'Offline' ? 'danger'
                    : 'warning'
                }
                minimal
            >
                {row.status}
            </Tag>
    },
];


const StatusControl = ({info, abortController, layout}) => {
    const [isOnline, setIsOnline] = useState(null);
    const [snapVersion, setSnapVersion] = useState(null);
    const [recidStatus, setRecidStatus] = useState(null);
    const [recidInfo, setRecidInfo] = useState([]);
    const [gridRows, setGridRows] = useState([]);
    const [streamTarget, setStreamTarget] = useState(null);

    const columns = React.useMemo(
        () => _buildRecordingColumns((row) => setStreamTarget(row)),
        []
    );

    useEffect(() => {
        if (info?.id)
            recordings.list({
                withinCameraServers: info.id, include: "processingConfig,singleThumbnail,statElems"
            }).then(recs => {
                console.log(`Loaded ${recs.length} recs for device ${info.name}`);
                setRecidInfo(recs);
            })

        pingDevice();
    }, [])

    // Merge recidInfo + recidStatus into grid rows
    useEffect(() => {
        if (!recidInfo.length) return;
        const rows = recidInfo.map(rec => {
            const cam_enabled = rec.processingConfig?.provider?.state === 'CAM_ON';
            let status;
            if (!cam_enabled) {
                status = 'Disabled';
            } else if (recidStatus === 'error') {
                status = 'Unknown';
            } else if (!Array.isArray(recidStatus)) {
                status = 'Checking';
            } else {
                const thisStatus = recidStatus.find(({recid: id}) => id === rec.id);
                status = thisStatus?.cam_state === 'SUCCESS' ? 'Online' : 'Offline';
            }
            return {
                ...rec,
                ip: _parseRtspIp(rec.processingConfig?.provider?.url),
                status,
            };
        });
        setGridRows(rows);
    }, [recidInfo, recidStatus]);

    const pingDevice = useCallback(() => {
        if (!!info) {
            console.log(`Pinging device ${info.name}`);
            setIsOnline(null);
            setSnapVersion(null);
            setRecidStatus(null);

            device.command(
                'HelloCommand', info.name, null, "command", {signal: abortController.signal}
            ).then((resp) => {
                setIsOnline(resp.payload?.resp === "hello!" ? "Online" : "Offline");
            }).catch((err) => {
                console.log(err);
                setIsOnline("Offline");
            })

            device.command(
                'GetSoftwareVersionCommand', info.name, null, "command", {signal: abortController.signal}
            ).then((resp) => {
                setSnapVersion(resp.payload?.resp ?? 'error');
            }).catch((err) => {
                console.log(err);
                setSnapVersion('error');
            })

            device.command(
                'GetRecidsStatusCommand', info.name, null, "command", {signal: abortController.signal}
            ).then((resp) => {
                setRecidStatus(resp.payload?.resp ?? []);
            }).catch((err) => {
                console.log(err);
                setRecidStatus('error');
            })
        }
    }, []);

    return (
        <>
            <StatusRow>
                <DeviceTopOverlay deviceName={info.name} abortController={abortController} />
                <Tooltip content="Click to ping again" fill disabled={!isOnline || !snapVersion}>
                    <StatusButton>
                        <Button
                            fill={true}
                            text={isOnline ?? 'Pinging...'}
                            intent={!isOnline ? "warning" : isOnline === "Online" ? "success" : "danger"}
                            onClick={pingDevice}
                            disabled={!isOnline || !snapVersion}
                        />
                    </StatusButton>
                </Tooltip>
                <Tooltip content="Click to ping again" fill disabled={!isOnline || !snapVersion}>
                    <StatusButton>
                        <Button
                            fill={true}
                            text={!snapVersion? "Getting Version..." : snapVersion === 'error'? "Unknown Version" : snapVersion}
                            intent={!snapVersion ? "warning" : snapVersion === "error" ? "danger" : "success"}
                            onClick={pingDevice}
                            disabled={!isOnline || !snapVersion}
                        />
                    </StatusButton>
                </Tooltip>
            </StatusRow>

            <SearchableDataGrid
                columns={layout === 'Mobile' ? columns.filter(c => c.key !== 'ip') : columns}
                rowHeight={150}
                data={gridRows}
                fitHeight
                rowClass={(row) => row.status === 'Online' ? 'row-cam-on' : row.status === 'Offline' ? 'row-cam-off' : 'row-cam-pending'}
            />
            <LiveStreamOverlay
                isOpen={!!streamTarget}
                onClose={() => setStreamTarget(null)}
                deviceName={info.name}
                recid={streamTarget?.id}
                recName={streamTarget?.name}
            />
        </>
    )
}


const ControlDevice = ({info, onRefresh}) => {
    const [isRebooting, setIsRebooting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [abortController] = useState(new AbortController());

    const {loading, setLoading} = useContext(DataPageContext);
    const layout = useLayout();

    useEffect(() => {
        return () => {
            abortController.abort("Aborting device commands because unmounting component");
        };
    }, [])

    const createRecid = useCallback(({name, img, width, height, source}) => {
        if (!info?.organisation || !info?.location || !info?.id) {
            errorToast({message: "The device must have an organisation and a location to create a recording"});
            return;
        }
        const currentNavContext = loading.location;
        setLoading({status: true, location: `Creating recid for device ${info.name}`});

        recordings.create({
            organisation: info.organisation,
            location: info.location,
            cameraServer: info.id,
            name: name,
            key: name.toLowerCase().replace(/\s+/g, '-'),
            type: 'auto-upload',
        }).then(rec => {
            successToast({message: `Created recording ${rec.id}`});
            return Promise.allSettled([
                recordings.thumbnail.patch(rec.id, {base64EncodedJpeg: img}),
                processingConfig.create(rec.id, {
                    provider: {state: 'CAM_ON', url: source, fps: 3, width, height},
                    Stats: {split_by: ['age', 'gender', 'lines', 'engagement', 'occupancy']},
                    Drawer: {enabled: false},
                    Audit: {enabled: false, schedule: '0-14 */3 * * *'},
                    Heatmaps: {enabled: true},
                    Spawner: {enabled_timed: false, enabled_line_crossing: false},
                }),
                statElems.create(rec.id, {
                    lines: {},
                    areas: {ignore: {}, trigger: {}},
                }),
            ]).then(([thumbResult, configResult, elemsResult]) => {
                if (thumbResult.status === 'rejected')
                    errorToast({message: 'Failed to set thumbnail'});
                if (configResult.status === 'rejected')
                    errorToast({message: 'Failed to create processing config'});
                if (elemsResult.status === 'rejected')
                    errorToast({message: 'Failed to create stat elems'});
                // Fire and forget — tell device to pick up the new recid
                device.command('UpdateHandledRecids', info.name, null, 'update').catch(() => {});
                setRefreshKey(k => k + 1);
                onRefresh();
            });
        }).catch(err => {
            errorToast({message: err?.message ?? 'Failed to create recording'});
            setLoading({status: false, location: currentNavContext});
        });
    }, []);

    return (
        <ContentWrapper>
            <TopLayout>
                <LeftPanel>
                    <StatusControl key={refreshKey} info={info} abortController={abortController} layout={layout} />
                </LeftPanel>
                <RightPanel>
                    <Card title="Thumbnails">
                        <ThumbnailsControl
                            deviceName={info.name}
                            onCreate={createRecid}
                        />
                    </Card>
                    <Card title="Network Control">
                        <NetworkControl
                            onGetInfo={() => device.command(
                                'GetNetworkInfoCommand', info.name, null, "command", {signal: abortController.signal}
                            ).then(resp => resp.payload?.resp)}
                            onSubmit={(config) => {
                                const payload = {
                                    ip: config.ip || "",
                                    netmask: config.netmask || "",
                                    gateway: config.gateway || "",
                                    dns: config.dns || "",
                                };
                                if (!window.confirm(
                                    `You are about to change network configuration for ${info.name}.\n` +
                                    `This might kick the device offline.\n\n` +
                                    `IP: ${payload.ip || '(empty)'}\nNetmask: ${payload.netmask || '(empty)'}` +
                                    (payload.gateway ? `\nGateway: ${payload.gateway}` : '') +
                                    (payload.dns ? `\nDNS: ${payload.dns}` : '') +
                                    `\n\nAre you sure?`
                                )) return Promise.resolve();
                                return device.command(
                                    'SetStaticONVIFCommand', info.name, payload, "command", {signal: abortController.signal}
                                ).then((resp) => {
                                    const isNack = resp.topic?.split('/').pop() === 'nack';
                                    const result = resp.payload?.resp;
                                    if (isNack)
                                        throw new Error(result?.error || 'Device returned an error');
                                    // newer devices return {success, message}, older ones return true/false
                                    if (result === false)
                                        throw new Error('Configuration failed');
                                    if (typeof result === 'object' && result?.success === false)
                                        throw new Error(result.message || 'Configuration failed');
                                    successToast({message: `Network configuration set on ${info.name}`});
                                }).catch((err) => {
                                    errorToast({message: `Failed to set network configuration on ${info.name}: ${err?.message ?? err}`});
                                    throw err;
                                });
                            }}
                        />
                    </Card>
                    <Card title="Welcome to the Danger Zone" intent="danger">
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.5em'}}>
                            <Button
                                fill={true}
                                icon="repeat"
                                text={isRebooting? "Rebooting..." : "Reboot device"}
                                intent="danger"
                                disabled={!info || isRebooting}
                                onClick={() => {
                                    if (window.confirm(`Are you sure you want to reboot ${info.name}?`)) {
                                        setIsRebooting(true);
                                        device.command(
                                            'DeviceRebootCommand', info.name, null, "command", {signal: abortController.signal}
                                        ).then((resp) => {
                                            console.log("Reboot requested");
                                        }).catch((err) => {
                                            console.log(err);
                                        }).finally(() => setIsRebooting(false));
                                    }
                                }}
                            />
                            <DecommissionDialog deviceName={info.name} deviceId={info.id} organisationId={info.organisation} locationId={info.location} locationName={info.locationName} />
                        </div>
                    </Card>
                </RightPanel>
            </TopLayout>
        </ContentWrapper>
    )
}

const OneDevControl = ({devid}) => {
    const [info, setInfo] = useState(null);
    const {setNav, setLoading} = useContext(DataPageContext);

    const loadDevice = useCallback(() => {
        setLoading({status: true, location: `Loading device ${devid}`});
        cameraServers.get(devid, {include: 'organisationDetails,locationDetails,recordings'}).then(apu => {
            setInfo({...apu, organisation: apu.organisation?._id, location: apu.location?._id, locationName: apu.location?.name});
            setNav({
                left: [
                    ...apu.organisation?.name ? [{
                        name: apu.organisation.name,
                        path: "/orgs/" + apu.organisation._id,
                        icon: "office",
                        subpaths: [
                            {name: "Locations", icon: "area-of-interest", path: '/locs?org=' + apu.organisation._id},
                            {name: "Devices", icon: "desktop", path: '/devs?org=' + apu.organisation._id},
                            {name: "Cameras", icon: "camera", path: '/recs?org=' + apu.organisation._id},
                            {name: "Users", icon: "people", path: '/orgs/' + apu.organisation._id + '/users'},
                        ]
                    }] : [],
                    ...apu.location?.name ? [{
                        name: apu.location.name,
                        path: "/locs/" + apu.location._id,
                        icon: "area-of-interest",
                        subpaths: [
                            {name: "Devices", icon: "desktop", path: '/devs?loc=' + apu.location._id},
                            {name: "Cameras", icon: "camera", path: '/recs?loc=' + apu.location._id},
                        ]
                    }] : []
                ],
                right: [
                    {name: "Cameras", path: "/recs?dev=" + apu.id, icon: "camera"},
                    {name: "Config", path: `/devs/${apu.id}/config`, icon: "cog"},
                ]
            });
            setLoading({status: false, location: `${apu.name} (${devid})`});
        }).catch(() => {
            setLoading({status: false, location: `Failed to load device ${devid}`});
        });
    }, []);

    useEffect(() => { loadDevice(); }, []);

    return !info ? null : <ControlDevice info={info} onRefresh={loadDevice} />;
}

export {ControlDevice, OneDevControl};